<script lang="ts">
  import type { FacetValue } from "../facet-value";
  import type { Facet, FacetOptions } from "./type.ts";
  import { List } from "../facet-value-group";

  import type { ClassValue } from "svelte/elements";
  import {
    addNameAndFeatureTracking,
    isNba,
    isNotPopular,
    isNotSelected,
    isPopular,
    isSelected,
  } from "./facetValues.ts";
  import { featureTrackingAttributes, removeStatusIf } from "../tracking";
  import { initToggles } from "../../toggle/Toggle.ts";
  import { setFilterTitleMode } from "../filter";

  interface Props extends Facet, FacetOptions {
    class?: ClassValue;
  }

  let {
    id,
    name,
    title,
    featureTracking,
    visibility,
    values = [],
    showTitle = false,
    filterTitleMode = "onlyTitle",
    mode = "default",
    parentId = "",
    hasNbaValues = false,
    contentSnippet = defaultList,
    ...rest
  }: Props = $props();

  /*                                               */
  initToggles();

  const facetId = `facet_${id}`;

  /*                                                                                                    */
  const hidden = visibility === "hidden";

  const namedValues = $derived(values.map(addNameAndFeatureTracking(facetId, name, 1)));

  /*                                                                                      */
  const selectedValues = $derived(values.filter(isSelected).map(addNameAndFeatureTracking(facetId, name, 1)));

  /*                                           */
  const popularValues = $derived(
    values.filter(isPopular).map(addNameAndFeatureTracking(facetId, name, selectedValues.length + 1)),
  );

  /*                                                                          */
  const unselectedNbas = $derived(
    values
      .filter(isNotPopular)
      .filter(isNotSelected)
      .filter(isNba)
      .map(addNameAndFeatureTracking(facetId, name, selectedValues.length + popularValues.length + 1)),
  );

  $effect(() => {
    if (filterTitleMode === "onlyPopularValuesAndTitle" && popularValues.length === 0) {
      setFilterTitleMode(parentId, "onlyTitle");
    } else {
      setFilterTitleMode(parentId, filterTitleMode);
    }
  });
</script>

{#snippet defaultList(values: FacetValue[])}
  <List {values}></List>
{/snippet}

<fieldset
  id={facetId}
  {name}
  class={["hc_facet", rest.class]}
  data-facet-visibility={visibility}
  data-has-nba-values={hasNbaValues}
  {...featureTrackingAttributes(removeStatusIf(hidden, featureTracking))}
  {hidden}
>
  {#if showTitle}
    <legend>
      <span>{title}</span>
    </legend>
  {/if}
  {#if mode === "sheetFirstLevel" && filterTitleMode === "onlyPopularValuesAndTitle" && !hidden}
    {@render contentSnippet(selectedValues.filter(isNotPopular), "selected")}
    {@render contentSnippet(unselectedNbas, "unselected")}
    {#if popularValues.length > 0}
      <div class="hc_facet__scrollBox">
        {@render contentSnippet(popularValues, "unselectedPopular")}
      </div>
    {/if}
  {:else}
    {@render contentSnippet(namedValues)}
  {/if}
</fieldset>

<style lang="scss">
  fieldset {
    min-width: 0;

    span {
      font: var(--oc-semantic-font-copy-100);
      font-weight: 700;
    }
  }
</style>
